import api from '@/services/axiosApi'
import type { PlanAction } from '../types'

/**
 * Available inline task types that map to backend RightyInlineService task types
 */
export type InlineTaskType =
  | 'create_question'
  | 'create_offer'
  | 'generate_code'
  | 'generate_variants'
  | 'generate_personalization'
  | 'generate_sync_values'
  | 'enrich_changes'
  | 'generate_publish_message'
  | 'generate_internal_id'
  | 'generate_segment_combinations'
  | 'question_analysis'
  | 'bootstrap_flow'

/**
 * Response types that can be returned from inline operations
 */
export type InlineResultType = 'plan' | 'json' | 'code' | 'raw'

/**
 * Result from an inline AI operation
 */
export interface InlineResult {
  success: boolean
  type: InlineResultType
  /** For plan type - the plan action to execute */
  plan?: PlanAction
  /** For json type - structured data */
  data?: Record<string, any>
  /** For code type - generated JavaScript code */
  code?: string
  /** For raw type or if no structured content found */
  content?: string
  /** Raw response content (always included) */
  rawContent?: string
  /** Error message if success is false */
  error?: string
}

/**
 * Request payload for inline operations
 */
export interface InlineRequest {
  /** The task type to execute */
  taskType: InlineTaskType
  /** The user's instruction */
  instruction: string
  /** Optional additional context for the task */
  context?: Record<string, any>
}

/**
 * Composable for inline/headless Righty AI operations.
 *
 * This provides focused, single-shot AI interactions without conversation history.
 * Used for task-specific AI operations like creating questions, generating code, etc.
 *
 * @example
 * ```typescript
 * const { execute, executeStreaming, isGenerating, streamingContent, result } = useRightyInline()
 *
 * // Non-streaming execution
 * const result = await execute({
 *   taskType: 'create_question',
 *   instruction: 'Create a question about their business size',
 *   context: { existingQuestions: ['Industry', 'Role'] }
 * })
 *
 * if (result.success && result.plan) {
 *   // Execute the plan
 *   await executePlan(result.plan)
 * }
 *
 * // Streaming execution
 * await executeStreaming({
 *   taskType: 'generate_code',
 *   instruction: 'Calculate total from quantity and price'
 * })
 *
 * // Watch streamingContent for real-time updates
 * watchEffect(() => {
 *   console.log(streamingContent.value)
 * })
 * ```
 */
export function useRightyInline(options?: { teamPid?: string }) {
  // Only try to get route if no teamPid was provided
  // This allows the composable to work outside component context when teamPid is passed directly
  const route = options?.teamPid ? null : useRoute()
  const teamPid = computed(() => options?.teamPid || (route?.params?.teamPid as string) || '')

  // State
  const isGenerating = ref(false)
  const streamingContent = ref('')
  const result = ref<InlineResult | null>(null)
  const error = ref<string | null>(null)

  /**
   * Execute an inline AI task (non-streaming).
   * Returns when the AI response is complete.
   */
  async function execute(request: InlineRequest): Promise<InlineResult> {
    if (!teamPid.value || teamPid.value === 'demo') {
      const errorResult: InlineResult = {
        success: false,
        type: 'raw',
        error: 'No team selected'
      }
      result.value = errorResult
      return errorResult
    }

    isGenerating.value = true
    streamingContent.value = ''
    error.value = null

    try {
      const { data } = await api.post(`/api/${teamPid.value}/righty/inline`, {
        taskType: request.taskType,
        instruction: request.instruction,
        context: request.context || {}
      })

      const inlineResult: InlineResult = {
        success: data.success,
        type: data.type || 'raw',
        plan: data.plan,
        data: data.data,
        code: data.code,
        content: data.content,
        rawContent: data.rawContent,
        error: data.error
      }

      // Normalize: if we have content but no data, try to parse content as JSON
      // This handles backends that return type: 'raw' with JSON in content
      if (!inlineResult.data && inlineResult.content) {
        try {
          inlineResult.data = JSON.parse(inlineResult.content)
        } catch {
          // Keep as raw content - not valid JSON
        }
      }

      result.value = inlineResult
      return inlineResult
    } catch (err: any) {
      const errorResult: InlineResult = {
        success: false,
        type: 'raw',
        error: err.response?.data?.error || err.message || 'Failed to execute inline task'
      }
      error.value = errorResult.error || null
      result.value = errorResult
      return errorResult
    } finally {
      isGenerating.value = false
    }
  }

  /**
   * Execute an inline AI task with SSE streaming.
   * Updates streamingContent in real-time as the response arrives.
   */
  async function executeStreaming(request: InlineRequest): Promise<InlineResult> {
    if (!teamPid.value || teamPid.value === 'demo') {
      const errorResult: InlineResult = {
        success: false,
        type: 'raw',
        error: 'No team selected'
      }
      result.value = errorResult
      return errorResult
    }

    isGenerating.value = true
    streamingContent.value = ''
    error.value = null

    try {
      // Get XSRF token from cookie (Laravel Sanctum pattern)
      const xsrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1]

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      }
      if (xsrfToken) {
        headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken)
      }

      const backendHost = import.meta.env.VITE_APP_BACKEND_HOST || ''
      const response = await fetch(`${backendHost}/api/${teamPid.value}/righty/inline/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          taskType: request.taskType,
          instruction: request.instruction,
          context: request.context || {}
        }),
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let fullText = ''
      let lastWasToolResult = false

      if (reader) {
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          // Parse SSE events from buffer
          const events = buffer.split('\n\n')
          buffer = events.pop() || '' // Keep incomplete event in buffer

          for (const event of events) {
            if (!event.trim()) continue

            // Parse event type and data
            const lines = event.split('\n')
            let eventType = ''
            let eventData = ''

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7)
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6)
              }
            }

            if (!eventData) continue

            try {
              const data = JSON.parse(eventData)

              switch (eventType) {
                case 'text_delta':
                  // Add paragraph break if resuming after a tool call
                  if (lastWasToolResult && fullText.length > 0) {
                    if (!fullText.endsWith('\n\n')) {
                      fullText += fullText.endsWith('\n') ? '\n' : '\n\n'
                    }
                  }
                  lastWasToolResult = false
                  fullText += data.delta || ''
                  streamingContent.value = fullText
                  break

                case 'tool_result':
                  lastWasToolResult = true
                  break

                case 'stream_end':
                  // Stream complete
                  break

                case 'error':
                  error.value = data.message || 'An error occurred'
                  break
              }
            } catch {
              // Ignore parse errors for malformed events
              console.warn('Failed to parse SSE event:', eventData)
            }
          }
        }
      }

      // Parse the final response to extract structured content
      const inlineResult = parseStreamingResponse(fullText)
      result.value = inlineResult
      return inlineResult
    } catch (err: any) {
      const errorResult: InlineResult = {
        success: false,
        type: 'raw',
        error: err.message || 'Failed to execute streaming inline task'
      }
      error.value = errorResult.error || null
      result.value = errorResult
      return errorResult
    } finally {
      isGenerating.value = false
    }
  }

  /**
   * Parse streaming response text to extract structured content (plan, config, json, code)
   */
  function parseStreamingResponse(responseText: string): InlineResult {
    // Check for plan blocks
    const planMatch = responseText.match(/```plan\s*([\s\S]*?)```/)
    if (planMatch) {
      try {
        const planData = JSON.parse(planMatch[1].trim())
        if (planData) {
          return {
            success: true,
            type: 'plan',
            plan: planData as PlanAction,
            rawContent: responseText
          }
        }
      } catch {
        // Fall through to other patterns
      }
    }

    // Check for JSON code blocks
    const jsonMatch = responseText.match(/```json\s*([\s\S]*?)```/)
    if (jsonMatch) {
      try {
        const jsonData = JSON.parse(jsonMatch[1].trim())
        if (jsonData) {
          return {
            success: true,
            type: 'json',
            data: jsonData,
            rawContent: responseText
          }
        }
      } catch {
        // Fall through to other patterns
      }
    }

    // Check for JavaScript code blocks
    const codeMatch = responseText.match(/```(?:javascript|js)\s*([\s\S]*?)```/)
    if (codeMatch) {
      return {
        success: true,
        type: 'code',
        code: codeMatch[1].trim(),
        rawContent: responseText
      }
    }

    // Return raw response if no structured content found
    return {
      success: true,
      type: 'raw',
      content: responseText,
      rawContent: responseText
    }
  }

  /**
   * Reset the composable state
   */
  function reset() {
    isGenerating.value = false
    streamingContent.value = ''
    result.value = null
    error.value = null
  }

  return {
    // State
    isGenerating: readonly(isGenerating),
    streamingContent: readonly(streamingContent),
    result: readonly(result),
    error: readonly(error),

    // Actions
    execute,
    executeStreaming,
    reset
  }
}
